import { Country, PaymentType } from '@afterpay/types'
import { useModal } from '@afterpaytouch/core'
import { Route } from '../../config/router'
import { PaymentOption, PaymentOptionsErrorModalKey, paymentOptionsErrors, TerminalErrorCode } from '../../model'
import { portalApiErrorFromPortalThunkRejectedValue } from '../../model/errors'
import {
  setCheckoutError,
  setPaymentOptionsError,
  setShowEfaModal,
  useAppDispatch,
  useAppRouter,
  useCountry,
  useCreditFreezeRetryContext,
  useIsAuNonAppleRetailCheckout,
  usePBIRejected,
  usePbiPaymentOption,
  usePaymentOptionsError,
  useIsPclOnlyOfferPage,
  useSelectedPaymentOption,
  useSelectedPaymentType,
} from '../../state'
import { useState } from 'react'
import { FetchVerificationErrorResponse } from '@afterpaytouch/event-verify'
import { ErrorCode } from '@afterpaytouch/portal-api'

interface PaymentOptionsErrorResponse {
  errorCode: ErrorCode
  errorInfo?: {
    sub_reason?: string
    maximum_order_value?: string
    credit_freeze_application_deadline?: string
  }
}

interface IUseHandlePaymentOptionsError {
  handlePaymentOptionsError: (e: unknown) => void
}

interface IUseSendEFARobocallEvent {
  onSuccess: () => void
  onError: (d: FetchVerificationErrorResponse) => void
  isSuccess: boolean
  isDecline: boolean
}

export const useHandlePaymentOptionsError = (): IUseHandlePaymentOptionsError => {
  const dispatch = useAppDispatch()
  const isPBIRejected = usePBIRejected()
  const isPclOnlyOfferPage = useIsPclOnlyOfferPage()
  const router = useAppRouter()
  const country = useCountry()
  const currentPaymentOptionsError = usePaymentOptionsError()
  const creditFreezeRetryContext = useCreditFreezeRetryContext()
  const { showModal: showPaymentOptionsErrorModal } = useModal(PaymentOptionsErrorModalKey)

  const handlePaymentOptionsError = (e: unknown): void => {
    const portalError = portalApiErrorFromPortalThunkRejectedValue(e) ?? (e as PaymentOptionsErrorResponse)
    const errorInfo = portalError.errorInfo as PaymentOptionsErrorResponse['errorInfo'] | undefined
    const errorKey = paymentOptionsErrors[portalError.errorCode] ?? TerminalErrorCode.ErrorPCL
    const subReason = errorKey === TerminalErrorCode.RiskDeclinedPCL ? errorInfo?.sub_reason : undefined
    const subErrorKey = subReason === 'credit_freeze' ? TerminalErrorCode.CreditFreezeDeclinedPCL : errorKey
    const maximumOrderValue = errorKey === TerminalErrorCode.AmountTooHighDeclinePCL ? errorInfo?.maximum_order_value : undefined
    const creditFreezeApplicationDeadline = errorInfo?.credit_freeze_application_deadline

    if (errorKey === TerminalErrorCode.ExtendedFraudAlert) {
      dispatch(setShowEfaModal(true))

      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
        })
      )
      return
    }

    const isCreditFreezeRetry429 =
      portalError.errorCode === ErrorCode.TooManyRequests &&
      (currentPaymentOptionsError?.error === TerminalErrorCode.CreditFreezeDeclinedPCL || creditFreezeRetryContext != null)

    if (isCreditFreezeRetry429) {
      const preservedDeadline = currentPaymentOptionsError?.extra?.creditFreezeApplicationDeadline ?? creditFreezeRetryContext?.deadline
      const preservedErrorCode = currentPaymentOptionsError?.errorCode ?? creditFreezeRetryContext?.errorCode ?? portalError.errorCode
      dispatch(
        setPaymentOptionsError({
          error: TerminalErrorCode.CreditFreezeDeclinedPCL,
          errorCode: preservedErrorCode,
          extra: {
            ...currentPaymentOptionsError?.extra,
            creditFreezeApplicationDeadline: preservedDeadline,
            tooManyRequests: true,
          },
        })
      )
      router.push(Route.RETRY_CREDIT_FREEZE)
      return
    }

    if (subErrorKey === TerminalErrorCode.CreditFreezeDeclinedPCL && creditFreezeApplicationDeadline != null && creditFreezeApplicationDeadline !== '') {
      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
          ...{ extra: { creditFreezeApplicationDeadline, tooManyRequests: false } },
        })
      )
      router.push(Route.RETRY_CREDIT_FREEZE)
      return
    }

    if (isPBIRejected || isPclOnlyOfferPage) {
      dispatch(
        setCheckoutError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
          extra: {
            maximumOrderValue,
          },
        })
      )

      router.push(Route.TERMINAL_ERROR)
    } else {
      if (country !== Country.AU) {
        showPaymentOptionsErrorModal()
      }
      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
          ...(maximumOrderValue !== undefined && { extra: { maximumOrderValue } }),
        })
      )
    }
  }
  return { handlePaymentOptionsError }
}

export const useSendEFARobocallEvent = (): IUseSendEFARobocallEvent => {
  const [efaRobocallSuccess, setEfaRobocallSuccess] = useState(false)
  const [efaRobocallDecline, setEfaRobocallDecline] = useState(false)

  const onSuccess = (): void => {
    setEfaRobocallSuccess(true)
    setEfaRobocallDecline(false)
  }
  const onError = (e: FetchVerificationErrorResponse): void => {
    setEfaRobocallSuccess(false)
    const error = e.message?.toLowerCase()
    if (error === 'declined') {
      setEfaRobocallDecline(true)
    }
  }

  const isSuccess = efaRobocallSuccess ?? false
  const isDecline = efaRobocallDecline ?? false
  return {
    onSuccess,
    onError,
    isSuccess,
    isDecline,
  }
}

export const useSelectedPaymentOptionState = (): [PaymentOption | undefined, (paymentOption: PaymentOption) => void] => {
  const selectedPaymentOption = useSelectedPaymentOption()
  const [selectedPaymentOptionLocal, setSelectedPaymentOptionLocal] = useState(selectedPaymentOption)

  return [selectedPaymentOptionLocal, setSelectedPaymentOptionLocal]
}

export const useOfferPreselectedOption = (firstOffer: PaymentOption | undefined): PaymentOption | undefined => {
  const isAuNonAppleRetailCheckout = useIsAuNonAppleRetailCheckout()
  const persistedPaymentOption = useSelectedPaymentOption()
  const selectedPaymentType = useSelectedPaymentType()
  const pbiPaymentOption = usePbiPaymentOption()

  if (isAuNonAppleRetailCheckout) {
    return persistedPaymentOption
  }
  const retainedPbiOption = selectedPaymentType === PaymentType.PBI ? pbiPaymentOption : undefined
  return persistedPaymentOption ?? retainedPbiOption ?? firstOffer
}
